/*
 * The hub's quick actions, as data: which a row offers, where a link goes,
 * and what each form sends. Pure, so every rule is tested without a screen
 * (quickActions.test.js); the forms in ./quick render them.
 */
import { startBody } from '../culture/PlateStartFields';
import { conditionsPayload } from '../PlateConditionsFields';
import { canDo } from './stages';
import { intakeNav } from '../../utils/linkedRecordNav';

/** Plate statuses a culture has finished in: no check or new action applies. */
export const TERMINAL_PLATE = ['failed', 'transferred_to_slants', 'transferred_to_plate'];

/**
 * A plate a check, a conditions entry or a lab photo can be aimed at: made
 * and not finished. A plate still awaiting creation does not exist on the
 * bench yet -- it is made (with the species' medium) by "Make waiting
 * plates" (PUT /plates/sample/{id}/activate), and a check logged on it first
 * would turn it into a growing plate with no medium. LabNotesTab hides the
 * same buttons on it.
 */
export const checkablePlate = (p) => !!p && !TERMINAL_PLATE.includes(p.status) && p.status !== 'awaiting_creation';

/**
 * Whether plates may be started from this isolate's origin: a collection or
 * intake that is submitted and live. A draft collection gets its two plates
 * when it is submitted (collections._auto_create_plates, which does not look
 * for one made by hand), so a plate started now would be a third; a deleted
 * origin takes none.
 */
export function plateableOrigin(item) {
  const detail = item?.pipeline?.collection?.detail;
  return !!(item?.sample_id || item?.external_id) && detail !== 'draft' && detail !== 'origin_deleted';
}

/* ── tubes ─────────────────────────────────────────────────────────────── */

/** A tube still in the lab's hands: not discarded, and not an archived past
 *  generation (mushroom_bank.is_archived). Nothing is grown from or banked
 *  out of any other. */
export const liveSlant = (s) => !!s && s.status !== 'discarded' && !s.is_archived;

/** A live tube not yet in the bank: it has no storage unit. */
export const unbankedSlant = (s) => liveSlant(s) && !s.storage_unit;

/** The set a plate's slants are cut as (utils.SLANT_DEFS, slant_codes_for):
 *  `${plate_code}-SA` to `-SD`, two active, one for refresh, one backup. */
export const SLANT_SET = [['A', 'active'], ['B', 'active'], ['C', 'refresh'], ['D', 'backup']];

/** The four tubes a deposit from this plate cuts, in the order the server
 *  cuts and places them -- the order `wells` is read in. */
export function cutSetTubes(plate) {
  if (!plate) return [];
  return SLANT_SET.map(([letter, role]) => {
    const code = `${plate.plate_code}-S${letter}`;
    return { key: code, code, role };
  });
}

/**
 * Whether a plate already has its slant set. The dossier says so
 * (has_slants); a row's plates do not, so there it is read off the
 * isolate's slants: one cut from the plate names it (plate_id) or carries
 * its code, `${plate_code}-S…`, as slant_codes_for writes it. Any slant
 * counts, a discarded one too: the server refuses a second set while one
 * exists, even a deleted one (slants.py _cut_set, 409 slants_exist).
 */
export function plateHasSlants(plate, slants = []) {
  if (typeof plate?.has_slants === 'boolean') return plate.has_slants;
  const prefix = plate?.plate_code ? `${plate.plate_code}-S`.toUpperCase() : null;
  return slants.some((s) => (!!s.plate_id && s.plate_id === plate?.plate_id)
    || (!!prefix && String(s.slant_code || '').toUpperCase().startsWith(prefix)));
}

/**
 * The plate statuses a deposit cuts a set from (slants.py
 * DEPOSITABLE_PLATE_STATUSES): made and still growing. A plate awaiting
 * creation, contaminated, failed or already transferred is refused, 409
 * plate_not_depositable.
 */
export const DEPOSITABLE_PLATE = ['pending', 'slow_growth', 'ready_to_transfer', 'in_rescue'];

/**
 * What a deposit to the bank can take: the isolate's live tubes not yet
 * stored, and the growing plates (DEPOSITABLE_PLATE) with no slant set to
 * cut one from. The dossier's lists when it is there (its plates say
 * has_slants), else the row's own.
 */
export function depositSources(item, dossier = null) {
  const slants = dossier?.slants || item?.slants || [];
  const plates = dossier?.plates || item?.plates || [];
  return {
    slants: slants.filter(unbankedSlant),
    plates: plates.filter((p) => DEPOSITABLE_PLATE.includes(p?.status) && !plateHasSlants(p, slants)),
  };
}

/** May this isolate be deposited to the bank: is there anything to put in? */
export function depositable(item) {
  const { slants, plates } = depositSources(item);
  return slants.length > 0 || plates.length > 0;
}

/**
 * The tube "Revive from bank" starts on: one in the bank (with a storage
 * unit) and healthy, the active one first -- that is the tube the bank keeps
 * to be grown from -- else any banked healthy tube, else any live tube.
 * Never a discarded or archived one: nothing is grown from it, and the
 * server refuses it (409 slant_not_usable). '' when there is none.
 */
export function reviveSlantId(slants = []) {
  const banked = slants.filter((s) => liveSlant(s) && s.storage_unit && s.status === 'healthy');
  const pick = banked.find((s) => s.slant_role === 'active') || banked[0] || slants.find(liveSlant);
  return pick ? pick.slant_id : '';
}

/**
 * The buttons a row's Actions cell offers, in order, by where the isolate
 * is (its population: one table lists them all, so the row says, not the
 * filter). `primary` is the row's default: "Deposit to bank" on the "ready
 * for bank" node, "Revive from bank" for a banked isolate with a tube to
 * grow from. Each is gated by the role sets the routes hold (stages.js).
 *
 * "Set storage" is not a row action of an isolate still in culture: on its
 * way to the bank, Deposit places every tube at once (and cuts the set when
 * there is none), and a single tube's place is set from the Bank tab.
 *
 * An isolate whose population is unknown (no lab visibility) is offered
 * nothing here; nor is one in the bank with no slant to act on -- kept only
 * as itself (specimen storage), or listed to a reader with no lab
 * visibility, who is shown no slants. Plates are started only from a
 * submitted, live origin (plateableOrigin).
 */
export function rowActions(item, user, { node } = {}) {
  const plates = item?.plates || [];
  const live = plates.filter(checkablePlate);
  const awaiting = plates.some((p) => p.status === 'awaiting_creation');
  // The waiting plates are made for their collection: not once it is deleted,
  // as no plate is started from a deleted origin (plateableOrigin).
  const activatable = awaiting && !!item.sample_id && item?.pipeline?.collection?.detail !== 'origin_deleted';
  const hasPlates = plates.length > 0 || item?.counts?.plates > 0;
  const plateable = plateableOrigin(item);
  const out = [];
  const add = (key, extra = {}) => {
    if (!canDo(user, key, item)) return false;
    out.push({ key, ...extra });
    return true;
  };
  switch (item?.population) {
    case 'active': {
      // On "ready for bank" the deposit leads; elsewhere it follows the
      // row's own next step, which stays the default.
      const banks = depositable(item);
      const bankFirst = node === 'to_bank' && banks && add('deposit', { primary: true });
      if (activatable) add('activate', { primary: !bankFirst });
      if (!hasPlates && plateable) add('startPlate');
      if (live.length) {
        add('logCheck', { primary: !bankFirst && !awaiting });
        add('labPhoto');
        add('conditions');
      }
      // Elsewhere the deposit follows the row's own next step -- or is it,
      // when there is none left: a plate gone to slants leaves loose tubes
      // and nothing else to do but bank them.
      if (banks && !bankFirst) add('deposit', { primary: !out.some((a) => a.primary) });
      break;
    }
    case 'bank': {
      // Every bank action is aimed at a slant. Growing the line again is
      // what a banked isolate is kept for, so it leads -- while a tube is
      // there to grow from. A plate it was revived onto is checked from
      // here too: a banked isolate stays in the bank lane while it grows.
      if (!item.slants?.length) break;
      const revived = item.slants.some(liveSlant) && add('revive', { primary: true });
      if (live.length) add('logCheck');
      add('setStorage', { primary: !revived });
      add('logReview');
      break;
    }
    case 'awaiting':
      // Nothing has reached the lab: its first plates, or the waiting ones made.
      if (activatable) add('activate', { primary: true });
      else if (!hasPlates && plateable) add('startPlate', { primary: true });
      break;
    default:
      break;
  }
  return out;
}

/* ── links ─────────────────────────────────────────────────────────────── */

/**
 * Where a row's gallery link goes. The Field gallery lists only the
 * person's own records, and its editor refuses anyone else's, so the owner
 * goes there and everyone else to the collection's own page; an intake goes
 * to its intake.
 */
export function galleryTarget(item, user) {
  if (!item) return null;
  if (item.external_id && !item.sample_id) {
    return intakeNav(item.external_id);
  }
  if (!item.sample_id) return null;
  if (user?.user_id && String(item.collector_id) === String(user.user_id)) {
    return { path: `/field/collection/${item.sample_id}`, state: { fromGallery: true, returnTo: '/' } };
  }
  return { path: '/collections', state: { openCollectionId: item.sample_id } };
}

/** The National Map, centred on the isolate's point; null without a fix. */
export function mapTarget(item, specimen) {
  const lat = specimen?.gps_lat ?? item?.gps_lat;
  const lng = specimen?.gps_lng ?? item?.gps_lng;
  if (lat === null || lat === undefined || lng === null || lng === undefined || lat === '' || lng === '') return null;
  return {
    path: '/map',
    state: { focus: { lat: Number(lat), lng: Number(lng), id: item?.sample_id || item?.external_id || null } },
  };
}

/* ── pickers ───────────────────────────────────────────────────────────── */

/**
 * The plate a plate action starts on: the server's default_plate_id (the
 * most recently checked live plate) when it can take a check, else the first
 * plate that can, else the first. A field isolate's two plates share a
 * created_at, so "newest" alone cannot choose; a plate awaiting creation is
 * never the default (checkablePlate).
 */
export function defaultPlateId(item, plates = item?.plates || []) {
  if (!plates.length) return '';
  const named = item?.default_plate_id && plates.find((p) => p.plate_id === item.default_plate_id);
  if (checkablePlate(named)) return named.plate_id;
  const live = plates.find(checkablePlate);
  return (live || plates[0]).plate_id;
}

/** The slant a bank action starts on: one not yet banked when the action
 *  banks, else the first. */
export function defaultSlantId(slants = [], { unbankedFirst = false } = {}) {
  if (!slants.length) return '';
  if (unbankedFirst) {
    const loose = slants.find((s) => !s.storage_unit && s.status !== 'discarded');
    if (loose) return loose.slant_id;
  }
  return slants[0].slant_id;
}

/* ── request bodies ────────────────────────────────────────────────────── */

const blank = (v) => v === undefined || v === null || String(v).trim() === '';
const numberOr = (v) => (blank(v) ? undefined : Number(v));
const textOr = (v) => (blank(v) ? undefined : String(v).trim());
const tidy = (body) => Object.fromEntries(Object.entries(body).filter(([, v]) => v !== undefined));

/** POST /plates/{id}/checks. A check with no status records conditions only;
 *  the plate's status stays as it was (plate_checks.py:193). */
export function checkBody(form) {
  const status = form.status || undefined;
  return tidy({
    status,
    contamination_type: status === 'contaminated' ? (form.contamination_type || undefined) : undefined,
    transfer_to: status === 'ready_to_transfer' ? (form.transfer_to || undefined) : undefined,
    colony_diameter_mm: numberOr(form.colony_diameter_mm),
    ph: numberOr(form.ph),
    humidity_pct: numberOr(form.humidity_pct),
    notes: textOr(form.notes),
    ...conditionsPayload(form),
  });
}

/** Why a check form cannot be sent yet, or null. */
export function checkProblem(form, { conditionsOnly = false } = {}) {
  if (!conditionsOnly && !form.status) return 'statusRequired';
  if (form.status === 'ready_to_transfer' && !form.transfer_to) return 'transferRequired';
  if (conditionsOnly) {
    const body = checkBody({ ...form, status: '' });
    if (Object.keys(body).length === 0) return 'nothingToRecord';
  }
  return null;
}

/**
 * PUT /plates/{id}: only the target temperature and notes that changed
 * (the route accepts notes, temperature_c and strain_id; plates.py:555).
 *
 * With `touched` (the form fields the person edited: 'temperature_c',
 * 'plate_notes'), a field nobody edited is never sent, whatever it holds: a
 * form seeded from a plate summary without notes must not clear the plate's
 * notes because a pH was recorded.
 */
export function plateEditBody(form, plate, touched = null) {
  const body = {};
  const edited = (field) => !touched || touched.has(field);
  if (edited('temperature_c')) {
    const temp = blank(form.temperature_c) ? null : Number(form.temperature_c);
    const was = plate?.temperature_c ?? null;
    if (temp !== (was === null ? null : Number(was))) body.temperature_c = temp;
  }
  if (edited('plate_notes')) {
    const notes = blank(form.plate_notes) ? null : String(form.plate_notes).trim();
    if (notes !== (blank(plate?.notes) ? null : plate.notes)) body.notes = notes;
  }
  return body;
}

/** POST /plates for an isolate with no plate yet. A collection takes the
 *  species' suggested medium when accepted, else a manual one
 *  (plates.py:308-338); an intake names its own (plates.py:215). */
export function startPlateBody(form, item) {
  const temperature = numberOr(form.temperature_c);
  if (item?.sample_id) {
    return tidy({
      origin: 'field_collection', sample_id: item.sample_id,
      accept_suggested_protocol: !!form.accept_suggested,
      manual_protocol: form.accept_suggested ? undefined : textOr(form.protocol),
      temperature_c: temperature,
      ...startBody(form.start),
    });
  }
  return tidy({
    origin: 'external_collection', external_id: item?.external_id,
    protocol: textOr(form.protocol), temperature_c: temperature,
    create_control_pair: form.control_pair ? true : undefined,
    ...startBody(form.start),
  });
}

/** PUT /plates/sample/{id}/activate: the waiting plates, made. */
export function activateBody(form) {
  return tidy({
    accept_suggested_protocol: !!form.accept_suggested,
    manual_protocol: form.accept_suggested ? undefined : textOr(form.protocol),
    temperature_c: numberOr(form.temperature_c),
    notes: textOr(form.notes),
    ...startBody(form.start),
  });
}

/** POST /dna from a culture: a plate or a slant, never both (dna.py:303). */
export function dnaBody(form) {
  const [kind, id] = String(form.source || '').split(':');
  return tidy({
    plate_id: kind === 'plate' ? id : undefined,
    slant_id: kind === 'slant' ? id : undefined,
    date_performed: textOr(form.date_performed),
    performed_by: textOr(form.performed_by),
    laboratory: textOr(form.laboratory),
    extraction_kit: textOr(form.extraction_kit),
    concentration_ng_ul: numberOr(form.concentration_ng_ul),
    a260_280: numberOr(form.a260_280),
    notes: textOr(form.notes),
  });
}

/** POST /dna/{id}/runs: a gene is required (dna_sequencing_runs.py:328). */
export function runBody(form) {
  return tidy({
    amplification_gene_id: textOr(form.amplification_gene_id),
    amplification_primers_id: textOr(form.amplification_primers_id),
    sequencing_primers_id: textOr(form.sequencing_primers_id),
    sequencing_provider_id: textOr(form.sequencing_provider_id),
    date_sent_for_sequencing: textOr(form.date_sent_for_sequencing),
    platform: textOr(form.platform),
    barcode: textOr(form.barcode),
    notes: textOr(form.notes),
  });
}

/** POST /collections/{id}/identifications: all four of REQUIRED
 *  (identifications.py:9); an approval only from an approver. */
export function identificationBody(form, user) {
  return tidy({
    taxon_name: textOr(form.taxon_name),
    taxonomic_rank: form.taxonomic_rank || undefined,
    confidence: form.confidence || undefined,
    identification_method: form.identification_method || undefined,
    expert_approval: form.expert_approval && canDo(user, 'approve') ? true : undefined,
    notes: textOr(form.notes),
  });
}

/** POST /slants/{id}/reviews (slants.py:382). */
export function reviewBody(form) {
  return tidy({
    status_at_review: form.status_at_review || undefined,
    action_taken: textOr(form.action_taken),
    notes: textOr(form.notes),
  });
}

/* ── a deposit to the bank (POST /slants/deposit) ──────────────────────── */

// utils.STORAGE_WELL: a row letter or two and a column number of up to three
// digits, from 1. Typed "c 7" or "C07", it is C7 to the server, one spelling
// per well (utils.canonical_well), so it is written that way here too.
const WELL = /^([A-Z]{1,2})(\d{1,3})$/;
const wellOf = (v) => String(v ?? '').toUpperCase().replace(/\s+/g, '')
  .replace(WELL, (text, row, column) => (Number(column) > 0 ? `${row}${Number(column)}` : text));

/**
 * The wells of `n` tubes placed side by side from `start`: 'C7' gives C7, C8,
 * C9, C10. Only the column counts on -- a box's next row is not ours to
 * guess -- and a column past the server's three digits is left blank for
 * the person to fill. A start that is not a well (column 0 included) fills
 * nothing.
 */
export function nextWells(start, n) {
  const m = WELL.exec(wellOf(start));
  return Array.from({ length: n }, (_, i) => {
    if (!m || Number(m[2]) === 0) return '';
    const column = Number(m[2]) + i;
    return column > 999 ? '' : `${m[1]}${column}`;
  });
}

/** The first well two of these tubes are given, written as the server
 *  compares them; null when every well is its own. */
export function duplicateWell(tubes = []) {
  const seen = new Set();
  for (const tube of tubes) {
    const well = wellOf(tube.well);
    if (!well) continue;
    if (seen.has(well)) return well;
    seen.add(well);
  }
  return null;
}

/**
 * POST /slants/deposit. `form` holds the source ('plate' or 'slants'), the
 * plate, the shared place (unit, Freezer ID, rack, box) and the one
 * preservation record; `tubes` are the tubes in the order they go in, each
 * with its own well ({slant_id?, code, well}). The wells are read in that
 * order (slants.py deposit_slants): a blank one is null, and when no tube
 * has one the key is left out. Only the parts of the place given are sent.
 */
export function depositBody(form, tubes = []) {
  const fromPlate = form.source === 'plate';
  const wells = tubes.map((tube) => wellOf(tube.well) || null);
  return tidy({
    plate_id: fromPlate ? (form.plate_id || undefined) : undefined,
    slant_ids: fromPlate ? undefined : tubes.map((tube) => tube.slant_id),
    place: tidy({
      unit: form.unit || undefined,
      unit_label: textOr(form.unit_label),
      rack: textOr(form.rack),
      box: textOr(form.box),
    }),
    wells: wells.some(Boolean) ? wells : undefined,
    preserved_at: textOr(form.preserved_at),
    preserved_by: textOr(form.preserved_by),
    preserved_organism: textOr(form.preserved_organism),
  });
}

/** Why a deposit cannot be sent yet, or null: the rules deposit_slants
 *  checks first, said before anything is cut. */
export function depositProblem(form, tubes = []) {
  if (!tubes.length || (form.source === 'plate' && !form.plate_id)) return 'tubesRequired';
  if (!form.unit) return 'unitRequired';
  if ([form.preserved_at, form.preserved_by, form.preserved_organism].some(blank)) return 'preservationRequired';
  if (duplicateWell(tubes)) return 'duplicateWell';
  return null;
}

/** A fresh Idempotency-Key (idempotency.py: 8-100 of A-Z a-z 0-9 . _ : -). */
export function idempotencyKey(prefix = 'hub') {
  const random = (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  return `${prefix}-${random}`.slice(0, 100);
}

/**
 * One key per submission: sending the same request again (a retry after the
 * answer was lost) reuses its key, so the server applies it once and replays
 * the first answer; a changed form is a new submission with a new key -- the
 * same key on a different body is refused (422). Every write route honours
 * the header (app.py:192-221).
 */
export function submissionKeys(prefix) {
  let last = null;
  return {
    keyFor(path, body) {
      const fingerprint = `${path} ${JSON.stringify(body)}`;
      if (!last || last.fingerprint !== fingerprint) last = { fingerprint, key: idempotencyKey(prefix) };
      return last.key;
    },
    // After a success the next send is a new submission even if it is identical.
    reset() { last = null; },
  };
}
